/** @jsxImportSource @emotion/react */
import merge from 'lodash/merge';
import { useEffect, useState } from 'react';

import {
  containerClass as checkboxContainerClass,
  countClassFn as checkboxCountClassFn,
  inputClass as checkboxInputClass,
  labelClass as checkboxLabelClass
} from '../Checkbox/Checkbox';

import type { Theme } from '@emotion/react';
import type { Interpolation } from '@emotion/serialize';
import type { TFilterOption } from '@stories/@types/custom';

const labelClass = (t: Theme) =>
  merge({}, checkboxLabelClass(t), {
    '::before': {
      borderRadius: '100%',
      boxSizing: 'border-box'
    },

    'input:checked ~ &': {
      '::before': {
        backgroundColor: 'white',
        border: `7px solid ${t.color.primary}`,
        content: '""'
      }
    },

    '.radioboxContainer:hover &': {
      color: t.color.primary
    }
  } as const);

type TRadioButtonProps = TFilterOption & {
  children?: React.ReactNode;
  cssProps?: Interpolation<Theme>;
  name: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const RadioButton: React.FC<TRadioButtonProps> = ({ children, checked, name, onChange, value, cssProps }) => {
  const [defaultChecked, setDefaultChecked] = useState(false);

  useEffect(() => {
    setDefaultChecked(checked || false);
  }, [checked]);

  return (
    <label css={{ ...checkboxContainerClass, ...(cssProps as object) }} className="radioboxContainer">
      <input
        css={checkboxInputClass}
        key={`${name}-${value}`}
        name={name}
        onChange={onChange}
        type="radio"
        value={value}
        checked={defaultChecked}
      />

      <span css={labelClass}>{children}</span>
    </label>
  );
};

const RadioButtonWithCount: React.FC<TRadioButtonProps> = ({ checked, ...props }) => {
  const [defaultChecked, setDefaultChecked] = useState(false);

  useEffect(() => {
    setDefaultChecked(checked || false);
  }, [checked]);

  return (
    <RadioButton {...props} checked={defaultChecked}>
      <span>{props.label}</span>
      {typeof props.count !== 'undefined' && (
        <>
          &nbsp;<span css={checkboxCountClassFn}>({props.count})</span>
        </>
      )}
    </RadioButton>
  );
};

export default RadioButton;
export { RadioButtonWithCount };
